import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { TLanguageIDs, TPriceStrategy } from 'types';
import { createPortionInitialState, CreatePortionContext, TTypeOfModalWindow } from './createPortionContext';
import { createPortionReducer } from './createPortionReducer';
import { CreatePortionActionTypes } from './createPortionActions';
import { IViewDishContext, ViewDishContext } from '../../../../viewDishState/viewDishContext';
import { v4 as uuidv4 } from 'uuid';

export const CreatePortionState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(createPortionReducer, createPortionInitialState);
	const { dishFullInfo, restaurantIdForPortions, restaurantIdForRestaurantProps } =
		useContext<IViewDishContext>(ViewDishContext);
	const restaurantOfPortion = restaurantIdForPortions ?? restaurantIdForRestaurantProps;

	useEffect(() => {
		if (dishFullInfo) {
			const defaultLanguage = dishFullInfo.translations.find((translation) => translation.isDefault);
			const defaultCustomerLanguage = defaultLanguage
				? {
						id: defaultLanguage.languageId,
						name: defaultLanguage.name || '',
				  }
				: null;

			const translations = dishFullInfo.translations.map((translation) => ({
				languageId: translation.languageId,
				name: '',
			}));

			dispatch({
				type: CreatePortionActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					fetchedCustomers: [dishFullInfo.customer],
					IDSelectedCustomer: dishFullInfo.customer.id,
					fetchedRestaurants: dishFullInfo.restaurants,
					IDSelectedRestaurant: restaurantOfPortion,
					nameOfDish: dishFullInfo.name,
					dishId: dishFullInfo.id,
					isCreatedOnPos: dishFullInfo.isCreatedOnPos,
					defaultCustomerLanguage,
					translations,
				},
			});
		}
	}, [dishFullInfo, restaurantOfPortion]);

	const changeNameOfPortion = useCallback((value: string) => {
		dispatch({
			type: CreatePortionActionTypes.CHANGE_NAME_OF_PORTION,
			payload: {
				nameOfPortion: value,
			},
		});
	}, []);

	const changePrice = useCallback((value: Nullable<number>) => {
		dispatch({
			type: CreatePortionActionTypes.CHANGE_PRICE,
			payload: {
				price: value,
			},
		});
	}, []);

	const changePriceStrategy = useCallback((value: TPriceStrategy) => {
		dispatch({
			type: CreatePortionActionTypes.CHANGE_PRICE_STRATEGY,
			payload: {
				priceStrategy: value,
			},
		});
	}, []);

	const changeExternalId = useCallback((value: string) => {
		dispatch({
			type: CreatePortionActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: value,
			},
		});
	}, []);

	const generateUniqueExternalId = useCallback(() => {
		const uniqueExternalId = uuidv4();
		dispatch({
			type: CreatePortionActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: uniqueExternalId,
			},
		});
	}, []);

	const toggleIsProtectedFromSync = useCallback(() => {
		dispatch({
			type: CreatePortionActionTypes.TOGGLE_IS_PROTECT_FROM_SYNC,
		});
	}, []);

	const toggleIsDisabled = useCallback(() => {
		dispatch({
			type: CreatePortionActionTypes.TOGGLE_IS_DISABLED,
		});
	}, []);

	// const setFetchedTranslations = useCallback(
	// 	(value: TLanguageIDs[], defaultCustomerLanguage: Nullable<IDefaultLanguage>) => {
	// 		const translations = value.map((id) => ({ languageId: id, name: '' }));
	// 		dispatch({
	// 			type: CreatePortionActionTypes.SET_FETCHED_TRANSLATIONS,
	// 			payload: {
	// 				translations,
	// 				defaultCustomerLanguage,
	// 			},
	// 		});
	// 	},
	// 	[],
	// );

	const setTranslationTab = useCallback((value: Nullable<TLanguageIDs>) => {
		dispatch({
			type: CreatePortionActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: CreatePortionActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const toggleApplyToOne = () => {
		dispatch({
			type: CreatePortionActionTypes.TOGGLE_APPLY_TO_ONE,
		});
	};

	const setAdditionalRestaurants = (value: string[]) => {
		dispatch({
			type: CreatePortionActionTypes.SET_ADDITIONAL_RESTAURANTS,
			payload: {
				selectedAdditionalRestaurants: value,
			},
		});
	};

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: CreatePortionActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<CreatePortionContext.Provider
			value={{
				changeNameOfPortion,
				changePrice,
				changePriceStrategy,
				changeExternalId,
				generateUniqueExternalId,
				toggleIsProtectedFromSync,
				toggleIsDisabled,
				// setFetchedTranslations,
				setTranslationTab,
				changeNameTranslation,
				toggleApplyToOne,
				setAdditionalRestaurants,
				changeTypeOfModalWindow,
				nameOfPortion: state.nameOfPortion,
				fetchedCustomers: state.fetchedCustomers,
				IDSelectedCustomer: state.IDSelectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				nameOfDish: state.nameOfDish,
				dishId: state.dishId,
				isCreatedOnPos: state.isCreatedOnPos,
				price: state.price,
				priceStrategy: state.priceStrategy,
				externalId: state.externalId,
				isProtectedFromSync: state.isProtectedFromSync,
				isDisabled: state.isDisabled,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				isApplyToOne: state.isApplyToOne,
				selectedAdditionalRestaurants: state.selectedAdditionalRestaurants,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</CreatePortionContext.Provider>
	);
};
